/**
 * OvertimeReport Page Component
 * 
 * Displays overtime (HHEE) report with indicators, filters, and detailed records
 * Design aligned with MyIncidents (indicators) and MyMonthlyAttendance (filters)
 */

import React, { useMemo } from 'react';
import PageLayout from '@/components/layout/PageLayout';
import { IndicatorCard } from '@/components/ui/cards/IndicatorCard';
import { DynamicFiltersCard, type FilterField } from '@/components/ui/cards/DynamicFiltersCard';
import { ExportDropdown } from '@/components/ui/filters/ExportDropdown';
import Table from '@/components/ui/tables/Table';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { IconScout } from '@/components/ui/images/IconScout';
import { Selector } from '@/components/ui/inputs/selector';
import { useOvertimeReport } from '@/hooks/overtime/useOvertimeReport';
import { useOvertimeExport } from '@/hooks/overtime/useOvertimeExport';
import type { OvertimeRecordType } from '@/api/domains/overtime';


export const OvertimeReport: React.FC = () => {
  const {
    filters,
    setFilters,
    handleSearch,
    records,
    indicators,
    totalRecords,
    isLoading,
    loadingMonthlyCuts,
    monthlyCutOptions,
    yearOptions,
    monthOptions,
    page,
    pageSize,
    setPage,
    setPageSize,
    onSortChange,
    columns,
    getExpandedFields,
    rowExpandable,
    translate,
  } = useOvertimeReport();

  const { exportOvertimeSummary, isExporting } = useOvertimeExport({
    monthlyCutId: filters.monthlyCutId,
    type: filters.type,
  });

  const handleDownload = (format: 'EXCEL' | 'PDF' | 'WORD') => {
    void exportOvertimeSummary(format);
  };

  // Type options
  const typeOptions = useMemo(
    () => [
      { value: 'Todos', label: translate('overtimeReport.filters.types.all') },
      { value: 'HHEE', label: translate('overtimeReport.filters.types.overtime') },
      { value: 'Atrasos', label: translate('overtimeReport.filters.types.delays') },
    ],
    [translate],
  );

  // Build filter fields for DynamicFiltersCard
  const filterFields: FilterField[] = useMemo(() => [
    {
      id: 'year',
      type: 'custom',
      colSpan: 1,
      loading: loadingMonthlyCuts,
      component: (
        <Selector
          label={translate('overtimeReport.filters.year')}
          value={filters.year ? String(filters.year) : ''}
          onChange={(value) => setFilters(previous => ({
            ...previous,
            year: Number(value),
            month: 0,
            monthlyCutCode: '',
            monthlyCutId: 0,
          }))}
          options={yearOptions}
          placeholder={translate('overtimeReport.filters.selectYear')}
          required
        />
      ),
    },
    {
      id: 'month',
      type: 'custom',
      colSpan: 1,
      loading: loadingMonthlyCuts,
      component: (
        <Selector
          label={translate('overtimeReport.filters.month')}
          value={filters.month ? String(filters.month) : ''}
          onChange={(value) => setFilters(previous => ({
            ...previous,
            month: Number(value) || 0,
            monthlyCutCode: '',
            monthlyCutId: 0,
          }))}
          options={monthOptions}
          disabled={!filters.year}
          placeholder={translate('overtimeReport.filters.selectMonth')}
          required
        />
      ),
    },
    {
      id: 'monthlyCutCode',
      type: 'custom',
      colSpan: 1,
      loading: loadingMonthlyCuts,
      component: (
        <Selector
          key={`monthly-cut-${filters.year}-${filters.month}`}
          label={translate('overtimeReport.filters.description')}
          value={filters.monthlyCutId ? filters.monthlyCutId.toString() : ''}
          onChange={(value) => {
            const stringValue = Array.isArray(value) ? value[0] : value;
            setFilters(previous => ({ 
              ...previous, 
              monthlyCutId: parseInt(stringValue, 10),
            }));
          }}
          options={monthlyCutOptions}
          disabled={!filters.year || !filters.month || monthlyCutOptions.length === 0}
          placeholder={translate('overtimeReport.filters.selectCutoff')}
          required
        />
      ),
    },
    {
      id: 'type',
      type: 'custom',
      colSpan: 1,
      component: (
        <Selector
          label={translate('overtimeReport.filters.type')}
          value={filters.type}
          onChange={(value) => setFilters(previous => ({ 
            ...previous, 
            type: value as OvertimeRecordType,
          }))}
          options={typeOptions}
          required
        />
      ),
    },
  ], [
    filters,
    setFilters,
    yearOptions,
    monthOptions,
    monthlyCutOptions,
    typeOptions,
    loadingMonthlyCuts,
    translate,
  ]);

  const canSearch = filters.year && filters.month && filters.monthlyCutId;

  return (
    <PageLayout>
      <div className="flex flex-col gap-6" data-testid="overtime-report-page">
        {/* Indicator Cards - Always visible, show 0 when no data */}
        <div className="flex flex-row gap-4 overflow-x-auto pb-4 px-2">
          <IndicatorCard
            label={translate('overtimeReport.indicators.overtimeFormatted')}
            value={indicators?.overtimeTotal || '00:00:00'}
            variant="default"
            data-testid="indicator-overtime-formatted"
          />
          <IndicatorCard
            label={translate('overtimeReport.indicators.overtimeDecimal')}
            value={indicators?.overtimeTotalDecimal.toFixed(4) || '0.0000'}
            variant="default"
            data-testid="indicator-overtime-decimal"
          />
          <IndicatorCard
            label={translate('overtimeReport.indicators.delaysFormatted')}
            value={indicators?.delaysTotal || '00:00:00'}
            variant="default"
            data-testid="indicator-delays-formatted"
          />
          <IndicatorCard
            label={translate('overtimeReport.indicators.delaysDecimal')}
            value={indicators?.delaysTotalDecimal.toFixed(4) || '0.0000'}
            variant="default"
            data-testid="indicator-delays-decimal"
          />
        </div>

        {/* Dynamic Filters Card - Same style as MyMonthlyAttendance */}
        <DynamicFiltersCard
          fields={filterFields}
          columns={4}
          actions={
            <>
              <PrimaryButton
                onClick={handleSearch}
                disabled={!canSearch || isLoading}
                isLoading={isLoading}
                iconLeft={<IconScout name="search" />}
                data-testid="button-search"
              >
                {translate('overtimeReport.actions.search')}
              </PrimaryButton>
              <ExportDropdown
                onExport={handleDownload}
                disabled={isLoading || isExporting || records.length === 0}
                placement="right"
                availableFormats={['EXCEL', 'PDF', 'WORD']}
              />
            </>
          }
        />

        {/* Table - Simplified columns per mockup */}
        <Table
          columns={columns}
          data={records}
          totalRows={totalRecords}
          isLoading={isLoading}
          searchable={true}
          serverSideSort
          onSortChange={onSortChange}
          pagination={{
            page,
            pageSize,
            total: totalRecords,
            onChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          initialSort={{ key: 'date', direction: 'asc' }}
          data-testid="table-overtime-report"
          expandable={{
            showExpandColumn: true,
            rowExpandable: rowExpandable,
            getExpandedFields: getExpandedFields,
          }}
        />
      </div>
    </PageLayout>
  );
};

export default OvertimeReport;
